import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { FormEvacZoneStatus, EvacZone, EvacZoneStatus } from 'shared/types';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { GeoEventRegion } from '../../geoEventRegion.types';
import { getEnabledEvacZoneStatuses } from '../../../evacZones/utils';
import {
  EVAC_ZONE_LABEL_TRANS_KEYS,
  EvacZoneStyles,
} from '../../../../constants';

export type UseRegionsChangeHandlerParams = {
  evacZoneStatuses: FormEvacZoneStatus[];
  regionEvacZones: EvacZone[];
  customEvacZoneStatuses: EvacZoneStatus[];
  evacZoneStyles: string[];
  unstructuredEvacuationRegionIds?: number[];
};

const getValidatedRegions = (props: {
  newRegions: GeoEventRegion[];
  currentRegions: GeoEventRegion[];
  evacZoneStatuses: FormEvacZoneStatus[];
  regionEvacZones: EvacZone[];
  customEvacZoneStatuses?: EvacZoneStatus[];
  unstructuredEvacuationRegionIds?: number[];
}): {
  validatedRegions: GeoEventRegion[];
  wouldOrphanZones?: string[];
  wouldOrphanStatuses?: EvacZoneStatus[];
  wouldOrphanUnstructuredRegions?: string[];
} => {
  const {
    newRegions,
    currentRegions,
    evacZoneStatuses,
    regionEvacZones,
    customEvacZoneStatuses,
    unstructuredEvacuationRegionIds = [],
  } = props;

  const newRegionIds = new Set(newRegions.map((r) => r.id));
  const currentRegionIds = new Set(currentRegions.map((r) => r.id));

  // Find regions being removed
  const removedRegionIds = [...currentRegionIds].filter(
    (id) => !newRegionIds.has(id),
  );

  // no regions were removed, return new regions
  if (removedRegionIds.length === 0) {
    return { validatedRegions: newRegions };
  }

  // ensure any custom evacuation levels are included in the orphan check
  const enabledStyles = newRegions.map(
    (region) => region.evacZoneStyle || EvacZoneStyles.default,
  );

  const enabledStatuses = getEnabledEvacZoneStatuses(enabledStyles);
  const wouldOrphanStatuses = customEvacZoneStatuses
    ? customEvacZoneStatuses.filter(
        (status) => !enabledStatuses.includes(status),
      )
    : [];

  // Removing a county that is flagged as having unstructured evacuations would
  // leave the flag pointing at a county that is no longer on the incident
  const wouldOrphanUnstructuredRegions = currentRegions
    .filter(
      (region) =>
        removedRegionIds.includes(region.id) &&
        unstructuredEvacuationRegionIds.includes(region.id),
    )
    .map((region) => region.displayName)
    .sort();

  // Only check zones if there are evacuation zone statuses
  if (evacZoneStatuses.length === 0) {
    if (
      wouldOrphanStatuses.length === 0 &&
      wouldOrphanUnstructuredRegions.length === 0
    ) {
      return { validatedRegions: newRegions };
    }
    return {
      validatedRegions: currentRegions,
      wouldOrphanStatuses,
      wouldOrphanUnstructuredRegions,
    };
  }

  // Check if any removed region has evacuation zone statuses
  // Create mapping from evacZone UID to region ID
  const uidToRegionId: { [key: string]: number } = regionEvacZones.reduce(
    (acc, zone) => {
      acc[zone.uidV2] = zone.region.id;
      return acc;
    },
    {} as { [key: string]: number },
  );

  // Find evacuation zones with status that belong to regions being removed
  const wouldOrphanZones = evacZoneStatuses
    .filter((status) => {
      const regionId = uidToRegionId[status.evacZone.uidV2];
      return removedRegionIds.includes(regionId);
    })
    .map((status) => status.evacZone.displayName)
    .sort();

  // If we have zones that would be orphaned with status, return original regions
  if (
    wouldOrphanZones?.length > 0 ||
    wouldOrphanStatuses?.length > 0 ||
    wouldOrphanUnstructuredRegions.length > 0
  ) {
    return {
      validatedRegions: currentRegions,
      wouldOrphanZones,
      wouldOrphanStatuses,
      wouldOrphanUnstructuredRegions,
    };
  }

  // Allow the region removal
  return { validatedRegions: newRegions };
};

export const useRegionsChangeHandler = (
  params: UseRegionsChangeHandlerParams,
): ((
  newRegions: GeoEventRegion[],
  currentRegions: GeoEventRegion[],
) => GeoEventRegion[]) => {
  const { t } = useTranslation();
  const {
    evacZoneStatuses,
    regionEvacZones,
    customEvacZoneStatuses,
    evacZoneStyles,
    unstructuredEvacuationRegionIds,
  } = params;
  // With the feature off there is no UI to unflag a county, so ignore saved ids
  // instead of blocking its removal.
  const unstructuredEvacuationsEnabled = useReleaseFlag(
    'unstructured_evacuations',
  );

  const handleRegionsChange = (
    newRegions: GeoEventRegion[],
    currentRegions: GeoEventRegion[],
  ): GeoEventRegion[] => {
    const {
      validatedRegions,
      wouldOrphanZones,
      wouldOrphanStatuses,
      wouldOrphanUnstructuredRegions,
    } = getValidatedRegions({
      newRegions,
      currentRegions,
      evacZoneStatuses,
      regionEvacZones,
      customEvacZoneStatuses,
      unstructuredEvacuationRegionIds: unstructuredEvacuationsEnabled
        ? unstructuredEvacuationRegionIds
        : undefined,
    });

    const orphans: string[] = [
      ...(wouldOrphanZones ?? []),
      ...(wouldOrphanStatuses?.map((status) =>
        t(
          EVAC_ZONE_LABEL_TRANS_KEYS[
            evacZoneStyles[0] as keyof typeof EVAC_ZONE_LABEL_TRANS_KEYS
          ][status],
        ),
      ) ?? []),
    ];

    if (orphans.length > 0) {
      enqueueSnackbar(
        `${t('editGeoEvent.inputs.counties.hasEvacZones', { orphans: orphans.join(', ') })}`,
        {
          variant: 'error',
        },
      );
    }

    if (wouldOrphanUnstructuredRegions?.length) {
      enqueueSnackbar(
        t('editGeoEvent.inputs.counties.hasUnstructuredEvacuations', {
          counties: wouldOrphanUnstructuredRegions.join(', '),
        }),
        {
          variant: 'error',
        },
      );
    }

    return validatedRegions;
  };

  return handleRegionsChange;
};
